/** @jsxImportSource davaux/client */
import { reactive } from 'davaux'
import { createSignal } from 'davaux/client'

/** A tick mark positioned along the slider track. */
export interface SliderMark {
  value: number
  label?: string
}

/** Props for the Slider component. */
export interface SliderProps {
  /** Initial value (SSR snapshot and client initial state). */
  value?: number
  /** Minimum selectable value; defaults to `0`. */
  min?: number
  /** Maximum selectable value; defaults to `100`. */
  max?: number
  /** Step between selectable values; defaults to `1`. */
  step?: number
  /** Tick marks shown along the track. */
  marks?: SliderMark[]
  /** Accent color applied to the filled track portion. */
  color?: string
  /** Controls track height and thumb size via design-token size steps. */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  /** Border-radius of the thumb. */
  radius?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | string | number
  /** When true, disables interaction. */
  disabled?: boolean
  /** Name submitted with the form. */
  name?: string
  /** Called with the new value whenever the thumb is dragged. */
  onChange?: (value: number) => void
  /** Additional CSS class names applied to the root element. */
  className?: string
  [key: string]: unknown
}

function Slider({
  value: initialValue,
  min = 0,
  max = 100,
  step = 1,
  marks,
  color,
  size,
  radius: r,
  disabled,
  name,
  onChange,
  class: className,
}: SliderProps) {
  const [val, setVal] = createSignal(initialValue ?? min)

  const pct = () => (max > min ? ((val() - min) / (max - min)) * 100 : 0)

  const rootStyle = () => {
    const vars: Record<string, string> = { '--dv-slider-fill': `${pct()}%` }
    if (color !== undefined) vars['--dv-slider-color'] = `var(--dv-color-${color}-filled)`
    if (size !== undefined) vars['--dv-slider-size'] = `var(--dv-slider-size-${size})`
    if (r !== undefined) {
      vars['--dv-slider-radius'] = typeof r === 'number' ? `${r}px` : `var(--dv-radius-${r}, ${r})`
    }
    return vars
  }

  function handleInput(e: Event) {
    const newVal = Number((e.target as HTMLInputElement).value)
    setVal(newVal)
    onChange?.(newVal)
  }

  return (
    <div
      class={`dv-slider${className ? ` ${className}` : ''}`}
      style={rootStyle}
      data-disabled={disabled || undefined}
    >
      <div class='dv-slider-track'>
        <input
          class='dv-slider-input'
          type='range'
          min={min}
          max={max}
          step={step}
          value={String(val())}
          name={name}
          disabled={disabled || undefined}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={() => val()}
          onInput={handleInput}
        />
      </div>
      {marks && marks.length > 0 && (
        <div class='dv-slider-marks'>
          {marks.map((m) => {
            const pctMark = max > min ? ((m.value - min) / (max - min)) * 100 : 0
            return (
              <>
                <div
                  class='dv-slider-mark'
                  style={{ '--dv-slider-mark-offset': `${pctMark}%` }}
                  data-filled={() => m.value <= val() || undefined}
                />
                {m.label !== undefined && (
                  <div
                    class='dv-slider-mark-label'
                    style={{ '--dv-slider-mark-offset': `${pctMark}%` }}
                  >
                    {m.label}
                  </div>
                )}
              </>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default reactive(Slider)
